import { isActiveSubscriptionStatus, isTrialSubscriptionStatus } from '@insolitum/types';
import { EkranStartowy, LogoInsolitum, ThemeToggle } from '@insolitum/ui';
import {
  LayoutDashboard,
  Building2,
  Users,
  Package,
  CreditCard,
  Settings,
  Menu,
  X,
  ChevronDown,
  LayoutGrid,
  HardHat,
  Ticket,
  ShoppingBag,
  Tags,
  Truck,
  Cog,
  Wrench,
  Network,
  Printer,
  FileStack,
  Sparkles,
  Waypoints,
  Handshake,
  GraduationCap,
  Search as SearchIcon,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react';
import { Suspense, useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';

import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { NotificationDropdown } from '../components/NotificationDropdown';
import { UserMenu } from '../components/UserMenu';
import { CommandPalette, useCommandPalette } from '../components/aurora/CommandPalette';
import { SupportSessionBanner } from '../components/support-access/SupportSessionBanner';
import { useSidebarCollapse } from '../hooks/useSidebarCollapse';
import { TRASY_SPRZEDAZY_SAMOOBSLUGOWEJ, ukryjSprzedazSamoobslugowa } from '../lib/fazaSprzedazy';
import { MODULE_NAV_ITEMS } from '../modules/registry';
import { prawaObiegu } from '../pages/admin/devices/obiegDokumentow';
import { useAuth } from '../providers/AuthProvider';
import { useModuleSubscriptionStore } from '../stores/moduleSubscriptionStore';

// All available modules with their metadata — single source of truth in the
// module registry (apps/universe-shell/src/modules/registry.ts).
const ALL_MODULES = MODULE_NAV_ITEMS;

// Feature flags — same semantics as JarvisDashboardPage / LivingNetworkPage.
const JARVIS_ENABLED = import.meta.env.VITE_GENERATIVE_UI_ENABLED !== 'false';
const LIVING_NETWORK_ENABLED = import.meta.env.VITE_LIVING_NETWORK_ENABLED !== 'false';

/**
 * Pudełko uciętej nazwy organizacji kończy się na wielokropku, a nie na
 * krawędzi miejsca w linii.
 *
 * Chromium stawia wielokropek tuż za ostatnią literą, która się zmieściła,
 * a pudełko `truncate` sięga do końca miejsca — reszta (do szerokości
 * następnej litery) zostawała pustą dziurą przed „· rolą”. Zmierzone przy
 * „OGYY SPÓŁKA Z OGRANICZONĄ…” w pasku 256 px, Inter 10,5 px: 7,2 px
 * dziury + 4 px `gap-1` = 12 px atramentu między wielokropkiem a „·”,
 * gdy zwykła spacja tego kroju daje 4,25 px. CSS nie zwęzi pudełka do
 * uciętej treści (ucięcie liczy się z szerokości pudełka), więc po ułożeniu
 * linii liczymy, gdzie wypadł wielokropek, i dajemy pudełku dokładnie tę
 * szerokość. Struktura zostaje: nazwa `truncate min-w-0`, rola `shrink-0`.
 *
 * Funkcja nie sięga do niczego spoza siebie — bramka
 * (`tests/unit/apps/pasekOrganizacjiBezMarki.test.tsx`) wykonuje ją
 * w Chromium na wyrenderowanym pasku.
 */
export function dosunDoWielokropka(nazwa: HTMLElement): void {
  nazwa.style.maxWidth = '';
  const tekst = nazwa.firstChild;
  if (!tekst || tekst.nodeType !== 3 || nazwa.scrollWidth <= nazwa.clientWidth) return;
  const kontekst = document.createElement('canvas').getContext('2d');
  if (!kontekst) return;
  kontekst.font = getComputedStyle(nazwa).font;
  const wielokropek = kontekst.measureText('\u2026').width;
  const miejsce = nazwa.getBoundingClientRect().width;
  const zakres = document.createRange();
  const szerokosc = (n: number) => {
    zakres.setStart(tekst, 0);
    zakres.setEnd(tekst, n);
    return zakres.getBoundingClientRect().width;
  };
  // Najdłuższy początek, który mieści się razem z wielokropkiem — ta sama
  // reguła, którą Chromium ucina tekst.
  let mieszczace = 0;
  let zaDuzo = (tekst as Text).data.length;
  while (mieszczace < zaDuzo) {
    const n = (mieszczace + zaDuzo + 1) >> 1;
    if (szerokosc(n) + wielokropek <= miejsce) mieszczace = n;
    else zaDuzo = n - 1;
  }
  // Ćwierć piksela zapasu: układ liczy w 1/64 px, a szerokość „na styk”
  // bywa o jedną literę za wąska — dziura wróciłaby w całej okazałości.
  nazwa.style.maxWidth = `${szerokosc(mieszczace) + wielokropek + 0.25}px`;
}

export function AdminLayout() {
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
  const { isCollapsed, toggleCollapse } = useSidebarCollapse();
  const { user, currentOrganization, organizations, partnerOrgId, switchOrganization, signOut } =
    useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [paletteOpen, setPaletteOpen] = useCommandPalette();

  // Ucięta nazwa organizacji kończy się na wielokropku (`dosunDoWielokropka`).
  // Linia zmienia szerokość z paskiem (rozwijanie), rola — z językiem, a krój
  // dochodzi po pierwszym malowaniu; każda z tych chwil liczy pudełko od nowa.
  const nazwaOrganizacjiRef = useRef<HTMLSpanElement>(null);
  const nazwaOrganizacji = currentOrganization?.organization.name;
  useLayoutEffect(() => {
    const nazwa = nazwaOrganizacjiRef.current;
    if (!nazwa || typeof ResizeObserver === 'undefined') return;
    const dopasuj = () => dosunDoWielokropka(nazwa);
    dopasuj();
    const obserwator = new ResizeObserver(dopasuj);
    if (nazwa.parentElement) obserwator.observe(nazwa.parentElement);
    if (nazwa.nextElementSibling) obserwator.observe(nazwa.nextElementSibling);
    document.fonts?.addEventListener?.('loadingdone', dopasuj);
    return () => {
      obserwator.disconnect();
      document.fonts?.removeEventListener?.('loadingdone', dopasuj);
    };
  }, [nazwaOrganizacji, isCollapsed]);

  // Konto z rolą partnera w dowolnej organizacji dostaje link do swojego panelu
  // — także członek zespołu partnera (admin/manager/employee w organizacji
  // partnera, `partnerOrgId`), dla którego panel partnera jest miejscem pracy.
  const isPartnerAccount = organizations.some((o) => o.role === 'partner') || !!partnerOrgId;

  // Use global store for module subscriptions (initialized by AuthProvider)
  const { subscriptions, animatingModules } = useModuleSubscriptionStore();

  // Track previous subscribed module IDs for exit animations
  const prevModuleIdsRef = useRef<Set<string>>(new Set());
  const [exitingModules, setExitingModules] = useState<Set<string>>(new Set());

  // Filter modules to only show subscribed/trial
  const subscribedModules = ALL_MODULES.filter((module) =>
    subscriptions.some((sub) => sub.module_id === module.id)
  );

  // Track entering/exiting modules for animations
  useEffect(() => {
    const currentIds = new Set<string>(subscribedModules.map((m) => m.id));
    const prevIds = prevModuleIdsRef.current;

    // Find modules that were removed (for exit animation)
    const removed = [...prevIds].filter((id) => !currentIds.has(id));
    if (removed.length > 0) {
      setExitingModules(new Set(removed));
      // Clear exiting modules after animation completes
      setTimeout(() => {
        setExitingModules(new Set());
      }, 400);
    }

    prevModuleIdsRef.current = currentIds;
  }, [subscribedModules]);

  // Combine subscribed modules with exiting modules for rendering
  const visibleModules = [
    ...subscribedModules,
    ...ALL_MODULES.filter(
      (m) => exitingModules.has(m.id) && !subscribedModules.some((s) => s.id === m.id)
    ),
  ];

  const obiegWidoczny = prawaObiegu({
    rola: currentOrganization?.role,
    statusCzlonkostwa: currentOrganization?.status,
    superAdmin: user?.is_super_admin === true,
  }).podglad;

  // Grupy nawigacji wg prototypu Aurora DS (Przegląd / Organizacja / Katalog /
  // System) — te same pozycje, trasy i flagi co wcześniej, tylko pogrupowane.
  const navGroups = [
    {
      label: 'Przegląd',
      items: [
        { name: t('nav.dashboard'), href: '/admin', icon: LayoutDashboard, end: true },
        // Organy AI (Fazy 3-4 masterplanu) — istniały jako trasy, ale były
        // nieosiągalne z żadnego menu; te same flagi co na stronach docelowych.
        ...(JARVIS_ENABLED
          ? [{ name: t('nav.jarvis', 'Jarvis'), href: '/admin/jarvis', icon: Sparkles }]
          : []),
        ...(LIVING_NETWORK_ENABLED
          ? [
              {
                name: t('nav.livingNetwork', 'Żywa sieć'),
                href: '/admin/living-network',
                icon: Waypoints,
              },
            ]
          : []),
      ],
    },
    // Grupa „Moduły" — druga od góry jak w prototypie V2 (Przegląd → Moduły →
    // Organizacja → System); dynamiczne moduły organizacji renderują się na jej
    // początku, a Marketplace zamyka grupę (statyczny wpis poniżej).
    {
      label: t('nav.modules'),
      items: [
        { name: t('nav.marketplace', 'Marketplace'), href: '/admin/modules', icon: Package },
        // Bez pozycji „Akademia": katalog zawodów przeniósł się do ARTIFEKSA
        // i wchodzi się do niego przez moduł, który renderuje się wyżej w tej
        // samej grupie (dynamiczne moduły organizacji). Drugi odnośnik z powłoki
        // prowadziłby do ekranu, którego powłoka już nie ma.
      ],
    },
    {
      label: 'Organizacja',
      items: [
        { name: 'Profil firmy', href: '/admin/company-profile', icon: Network },
        // Akademia Universe UŻYTKOWNIKA (nie partnera — patrz komentarz
        // w grupie „System"): prezentacja organów głosem Nexusa i postęp
        // per osoba, dla właściciela i każdego zaproszonego pracownika.
        { name: 'Akademia Universe', href: '/admin/akademia', icon: GraduationCap },
        { name: t('nav.sites'), href: '/admin/sites', icon: Building2 },
        { name: t('nav.users'), href: '/admin/users', icon: Users },
        { name: 'Pracownicy', href: '/admin/workers', icon: HardHat },
        // Dział zbiera CZTERY rodziny sprzętu (telewizory, urządzenia
        // mobilne, stanowiska produkcyjne, serwery wydruku) i instalatory
        // do nich — stąd nazwa i ikona siatki zamiast telefonu. Adres
        // zostaje `/admin/devices`: linkują do niego maile zdarzeń
        // (`pocztaZdarzen/mapa.ts`) i komunikat błędu panelu produkcyjnego.
        { name: 'Aplikacje i urządzenia', href: '/admin/devices', icon: LayoutGrid },
        // Obieg dokumentów (UD-12a, plan 53 §11.2) — ekran pracy, więc POZA
        // filtrem sprzedaży samoobsługowej. Widzą go ci, którym baza odda
        // dokumenty: owner, admin, manager z AKTYWNYM członkostwem i super-admin
        // (`is_org_writer`). Menu nie jest zabezpieczeniem — strona sama mówi
        // „brak prawa”. `href` bez `?tab=`: okruszki porównują `pathname`.
        ...(obiegWidoczny
          ? [{ name: 'Obieg dokumentów', href: '/admin/obieg', icon: FileStack }]
          : []),
        // Centralny organ druku (kartoteka infrastrukturalna, nie moduł) —
        // /admin/printing, plan PLAN_REALIZACJI_ORGAN_DRUKU §7.
        { name: t('nav.printing', 'Drukowanie'), href: '/admin/printing', icon: Printer },
      ],
    },
    {
      label: 'Katalog',
      items: [
        { name: 'Produkty', href: '/admin/products', icon: ShoppingBag },
        { name: 'Kategorie', href: '/admin/categories', icon: Tags },
        { name: 'Dostawcy', href: '/admin/suppliers', icon: Truck },
        { name: 'Sprzęt', href: '/admin/equipment', icon: Cog },
        { name: 'Serwisanci', href: '/admin/technicians', icon: Wrench },
      ],
    },
    {
      label: 'System',
      items: [
        // Kolejność wg prototypu V2 (Aurora DS.html): … → Ustawienia → Wsparcie.
        { name: t('nav.billing'), href: '/admin/billing', icon: CreditCard },
        // Bez „Akademii Nexusa" i „Moich premii": to funkcje PANELU PARTNERA
        // (/partner/akademia, /partner/premie) — Akademia to onboarding
        // partnerski, a premie przyznaje partner ze swojej prowizji. W panelu
        // tenanta obie strony kończyły się odmową albo wiecznie pustą tabelą.
        { name: t('nav.settings'), href: '/admin/settings', icon: Settings },
        { name: 'Wsparcie', href: '/admin/support', icon: Ticket },
      ],
    },
  ];
  // Faza 1 soft startu: samoobsługowa sprzedaż (Marketplace, Billing) ukryta —
  // kolejne organy sprzedaje opiekun ofertą. Super-admin widzi wszystko, a po
  // przejściu na plan płatny pozycje wracają same (`lib/fazaSprzedazy.ts`).
  const bezSprzedazy = ukryjSprzedazSamoobslugowa(currentOrganization?.organization, user);
  const navGroupsWidoczne = navGroups.map((g) => ({
    ...g,
    items: bezSprzedazy
      ? g.items.filter(
          (i) => !(TRASY_SPRZEDAZY_SAMOOBSLUGOWEJ as readonly string[]).includes(i.href)
        )
      : g.items,
  }));
  const navigation = navGroupsWidoczne.flatMap((g) =>
    g.items.map((i) => ({ ...i, section: g.label }))
  );

  // Okruszki topbara — nazwa bieżącej strony z nawigacji / modułów (wizualne).
  const modulePage = location.pathname.startsWith('/admin/modules/')
    ? ALL_MODULES.find((m) => location.pathname.startsWith(`/admin/modules/${m.id}`))?.name
    : undefined;
  const navPage = navigation
    .filter((i) => (i.end ? location.pathname === i.href : location.pathname.startsWith(i.href)))
    .sort((a, b) => b.href.length - a.href.length)[0]?.name;
  const currentPage = modulePage ?? navPage ?? t('nav.dashboard');

  // Widoki pełnoekranowe — kanwa wypełnia całą powierzchnię pod topbarem
  // (bez max-width/paddingu .page), zamiast siedzieć w karcie w karcie.
  const isBleedPage = location.pathname.startsWith('/admin/living-network');

  const paletteItems = [
    ...navigation,
    ...visibleModules.map((m) => ({
      name: m.name || t(`modules.${m.id}`),
      href: `/admin/modules/${m.id}?org_id=${currentOrganization?.organization_id || ''}`,
      icon: m.icon,
      section: t('nav.modules'),
    })),
  ];

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  // Szerokości paska = te same, co w pasku organu i w panelach partnera
  // i super-admina (256 / 64). Prototyp miał 272 / 76, przez co zwinięty
  // pasek powłoki stawiał ikony na osi 38, a organ na 32 — przejście
  // powłoka → organ przesuwało całą kolumnę ikon o 6 px.
  const sidebarWidth = isCollapsed ? 'w-16' : 'w-64';
  const mainPadding = isCollapsed ? 'lg:pl-16' : 'lg:pl-64';

  return (
    <div className="min-h-screen bg-aurora-bg">
      {/* Wejście partnera na konto klienta (za zgodą, migracja 459) — pasek
          renderuje się TYLKO wtedy, gdy taka sesja trwa, i nagrywa nawigację. */}
      <SupportSessionBanner />
      {/* Mobile sidebar backdrop — .scrim z DS (tokenowe tło + blur, jak modale);
          z-40 nadpisuje --ids-z-overlay, żeby drawer (z-50) został nad scrimem. */}
      {sidebarOpen && (
        <div
          className="scrim show z-40 lg:hidden touch-none overscroll-none"
          aria-hidden="true"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 ${sidebarWidth} bg-aurora-bg-subtle border-r border-aurora-border transform transition-all duration-300 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Sidebar top — .sidebar__top (64 px, wcięcie 12, dolna linia — oś
              nagłówka paska organu): sygnet + sidebar__id, gdzie sidebar__role
              = „nazwa tenanta · rola". Bez boksu
              switchera poniżej — cały blok identyfikacji jest triggerem dropdownu
              organizacji (ten sam stan orgDropdownOpen i switchOrganization). */}
          <div className="sidebar__top relative">
            {isCollapsed ? (
              // Goły sygnet, nie kafel: kafel z gradientem akcentu niesie
              // tożsamość organu (`.sidebar__mark` w modułach), a powłoka to
              // Insolitum — „kafel to organ, goły znak to Insolitum".
              <button
                onClick={() => setOrgDropdownOpen(!orgDropdownOpen)}
                className="sidebar__sygnet sidebar__sygnet--przycisk mx-auto"
                title={currentOrganization?.organization.name}
                aria-label={
                  currentOrganization?.organization.name ??
                  t('nav.switchOrganization', 'Przełącz organizację')
                }
                aria-expanded={orgDropdownOpen}
              >
                <LogoInsolitum uklad="sygnet" tytul={null} />
              </button>
            ) : (
              <>
                <span className="sidebar__sygnet">
                  <LogoInsolitum uklad="sygnet" tytul={null} />
                </span>
                <button
                  onClick={() => setOrgDropdownOpen(!orgDropdownOpen)}
                  className="sidebar__id flex-1 min-w-0 text-left"
                  aria-expanded={orgDropdownOpen}
                >
                  <span className="sidebar__name">Universe</span>
                  <span className="sidebar__role flex items-center">
                    {/* Bez organizacji (super-admin, który nie należy do żadnej)
                        linia mówi to wprost. Zapas „Insolitum · Administrator”
                        był dwiema wartościami zastępczymi brzmiącymi jak dane:
                        słowo marki krojem interfejsu przy gołym sygnecie
                        udawało nazwę firmy, a rola — rolę, której to konto
                        w żadnej organizacji nie ma. */}
                    {/* Nazwa i rola to DWA elementy: przy długiej nazwie ucina
                        się nazwa (`truncate min-w-0`), a rola zostaje widoczna
                        (`shrink-0`). W jednym `truncate` ucinała się zawsze
                        końcówka, czyli rola. Odstęp nazwa → „·” to zwykła
                        spacja tego kroju: twarda spacja na początku roli (nie
                        `gap`, nie zwykła spacja — ta na początku elementu
                        znika), a ucięte pudełko kończy się na wielokropku. */}
                    {currentOrganization ? (
                      <>
                        <span ref={nazwaOrganizacjiRef} className="truncate min-w-0">
                          {currentOrganization.organization.name}
                        </span>
                        <span className="shrink-0">
                          {`\u00A0· ${t(`roles.${currentOrganization.role}`)}`}
                        </span>
                      </>
                    ) : (
                      <span className="truncate">{t('nav.bezOrganizacji', 'Bez organizacji')}</span>
                    )}
                    <ChevronDown
                      className={`ml-1 w-3 h-3 flex-shrink-0 transition-transform ${orgDropdownOpen ? 'rotate-180' : ''}`}
                    />
                  </span>
                </button>
              </>
            )}
            <button
              onClick={() => setSidebarOpen(false)}
              className="iconbtn lg:hidden ml-auto"
              aria-label={t('common.close')}
            >
              <X className="w-6 h-6" />
            </button>

            {currentOrganization && orgDropdownOpen && organizations.length > 1 && (
              <div className="absolute left-2 top-full mt-1 w-60 bg-aurora-raised rounded-ids-md shadow-elev-3 border border-aurora-border-strong py-1 z-10 animate-in zoom-in-95">
                {organizations.map((org) => (
                  <button
                    key={org.organization_id}
                    onClick={() => {
                      switchOrganization(org.organization_id);
                      setOrgDropdownOpen(false);
                    }}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-aurora-surface transition-colors ${
                      org.organization_id === currentOrganization?.organization_id
                        ? 'bg-aurora-surface'
                        : ''
                    }`}
                  >
                    <div className="w-6 h-6 rounded bg-aurora-brand/15 flex items-center justify-center">
                      <Building2 className="w-3 h-3 text-aurora-brand" />
                    </div>
                    <span className="text-sm text-aurora-text truncate">
                      {org.organization.name}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Navigation */}
          <nav className="navscroll flex-1 px-3 py-4 space-y-1 overflow-y-auto overscroll-contain">
            {navGroupsWidoczne.map((group) => (
              <div key={group.label}>
                {!isCollapsed && <p className="navgroup__lbl">{group.label}</p>}
                {/* Dynamiczne moduły organizacji otwierają grupę „Moduły" (kompozycja
                    V2: Przegląd → Moduły → Organizacja → System); statyczny wpis
                    Marketplace z group.items zamyka grupę poniżej. */}
                {group.label === t('nav.modules') &&
                  visibleModules.map((module) => {
                    const isEntering =
                      animatingModules.has(module.id) &&
                      subscribedModules.some((m) => m.id === module.id);
                    const isExiting = exitingModules.has(module.id);

                    // Badge statusu subskrypcji (Aurora DS) — wyłącznie z danych,
                    // które layout już ma w moduleSubscriptionStore (status +
                    // trial_ends_at); brak rekordu/statusu = brak badge.
                    //
                    // Trial rozpoznaje wspólny strażnik z `@insolitum/types`,
                    // a nie porównanie z literałem `'trial'`: kanoniczną
                    // pisownią jest `'trialing'`, a `'trial'` to zaszłość
                    // (module.ts:68-78). Porównanie z jednym literałem znaczyło
                    // BRAK plakietki na trialu zapisanym kanonicznie.
                    const subscription = subscriptions.find((sub) => sub.module_id === module.id);
                    const naTrialu = isTrialSubscriptionStatus(subscription?.status);
                    const trialDaysLeft =
                      naTrialu && subscription?.trial_ends_at
                        ? Math.max(
                            0,
                            Math.ceil(
                              (new Date(subscription.trial_ends_at).getTime() - Date.now()) /
                                86_400_000
                            )
                          )
                        : null;

                    return (
                      <NavLink
                        key={module.id}
                        to={`/admin/modules/${module.id}?org_id=${currentOrganization?.organization_id || ''}`}
                        onClick={() => setSidebarOpen(false)}
                        title={isCollapsed ? module.name : undefined}
                        className={({ isActive }) =>
                          `navitem transition-all duration-300 ${
                            isCollapsed ? 'justify-center' : ''
                          } ${isActive ? 'is-active' : ''} ${
                            isEntering
                              ? 'animate-module-enter'
                              : isExiting
                                ? 'animate-module-exit'
                                : ''
                          }`
                        }
                        style={{
                          animationFillMode: 'forwards',
                        }}
                      >
                        <module.icon
                          className={`w-5 h-5 flex-shrink-0 ${module.color} ${
                            isEntering ? 'animate-module-icon-pulse' : ''
                          }`}
                        />
                        {!isCollapsed && (
                          <span className={isEntering ? 'animate-module-text-slide' : ''}>
                            {module.name || t(`modules.${module.id}`)}
                          </span>
                        )}
                        {!isCollapsed && isActiveSubscriptionStatus(subscription?.status) && (
                          <span className="badge badge--brand">LIVE</span>
                        )}
                        {!isCollapsed && naTrialu && (
                          <span className="badge">
                            Trial{trialDaysLeft !== null ? ` ${trialDaysLeft}d` : ''}
                          </span>
                        )}
                      </NavLink>
                    );
                  })}
                {group.items.map((item) => (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    end={item.end}
                    onClick={() => setSidebarOpen(false)}
                    title={isCollapsed ? item.name : undefined}
                    className={({ isActive }) =>
                      `navitem ${isCollapsed ? 'justify-center' : ''} ${isActive ? 'is-active' : ''}`
                    }
                  >
                    <item.icon className="w-5 h-5 flex-shrink-0" />
                    {!isCollapsed && item.name}
                  </NavLink>
                ))}
              </div>
            ))}

            {/* Panel Partnera — symetryczny link zwrotny; bez niego partner
                zalogowany na głównej domenie nigdy nie odkryje swojego panelu. */}
            {isPartnerAccount && (
              <div className="pt-4 mt-4 border-t border-aurora-border">
                <NavLink
                  to="/partner"
                  onClick={() => setSidebarOpen(false)}
                  title={isCollapsed ? t('nav.partnerPanel', 'Panel Partnera') : undefined}
                  className={({ isActive }) =>
                    `navitem ${isCollapsed ? 'justify-center' : ''} ${
                      isActive
                        ? 'bg-aurora-warning/20 text-aurora-warning'
                        : 'text-aurora-warning/80 hover:text-aurora-warning'
                    }`
                  }
                >
                  <Handshake className="w-5 h-5 flex-shrink-0" />
                  {!isCollapsed && t('nav.partnerPanel', 'Panel Partnera')}
                </NavLink>
              </div>
            )}
          </nav>

          {/* Zwiń/Rozwiń w drawerze mobilnym — desktopowy toggle żyje w topbarze
              (V2), ale stan isCollapsed jest trwały (localStorage), więc <lg
              drawer musi mieć własny sposób rozwinięcia zwiniętego menu (jak
              przed zmianą i jak PartnerLayout/SuperAdminLayout). */}
          <div className="lg:hidden px-2 py-2 border-t border-aurora-border">
            <button
              onClick={toggleCollapse}
              className={`navitem ${isCollapsed ? 'justify-center' : ''}`}
              title={
                isCollapsed
                  ? t('nav.expandMenu', 'Rozwiń menu')
                  : t('nav.collapseMenu', 'Zwiń menu')
              }
            >
              {isCollapsed ? (
                <PanelLeftOpen className="w-5 h-5 flex-shrink-0" />
              ) : (
                <>
                  <PanelLeftClose className="w-5 h-5 flex-shrink-0" />
                  <span>{t('nav.collapseMenu', 'Zwiń menu')}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`${mainPadding} transition-all duration-300`}>
        {/* Top Bar */}
        <header className="topbar z-30">
          <button
            onClick={() => setSidebarOpen(true)}
            className="iconbtn lg:hidden"
            aria-label="Otwórz menu"
          >
            <Menu className="w-6 h-6" />
          </button>

          {/* Zwijanie sidebara — pierwszy element topbara jak w prototypie V2
              (Aurora DS.html: iconbtn data-collapse z hamburgerem). */}
          <button
            onClick={toggleCollapse}
            className="iconbtn hidden lg:grid"
            aria-label={t('nav.collapseMenu', 'Zwiń menu')}
          >
            <Menu className="w-5 h-5" />
          </button>

          <div className="topbar__crumbs hidden sm:flex">
            Universe<span>›</span>
            <b>{currentPage}</b>
          </div>

          <button
            type="button"
            className="cmdk hidden md:flex"
            onClick={() => setPaletteOpen(true)}
            aria-label="Szukaj (Ctrl/Cmd+K)"
          >
            <SearchIcon />
            <span>{t('nav.searchPlaceholder', 'Szukaj strony, modułu, akcji…')}</span>
            <kbd>⌘K</kbd>
          </button>

          <div className="topbar__right">
            <LanguageSwitcher variant="minimal" />
            {/* Tryb jasny działa od chwili, gdy ThemeProvider ustawia `data-mode`
                na <html>; rampa neutralna przełącza się razem z nim. */}
            <ThemeToggle size="sm" />
            <NotificationDropdown />
            {/* Jedyne miejsce zalogowanego użytkownika: awatar + menu (rola,
                ustawienia, wylogowanie). Stopki w sidebarze nie ma — patrz UserMenu. */}
            <UserMenu
              imie={user?.full_name}
              email={user?.email}
              rola={t(`roles.${currentOrganization?.role ?? 'admin'}`)}
              ustawieniaHref="/admin/settings"
              onSignOut={handleSignOut}
            />
          </div>
        </header>

        {/* Page Content — .page z DS (max-width 1480px, wyśrodkowanie, paddingi
            tokenowe) jak .scrollarea > .page w prototypie V2. Żywa sieć jest
            widokiem pełnoekranowym (.page--bleed): zajmuje całą powierzchnię
            pod topbarem, bez ramki w ramce. */}
        <main className={isBleedPage ? 'page page--bleed' : 'page'}>
          {/* Leniwy kawałek strony czeka W TREŚCI: pasek boczny i nagłówek
            zostają. Bez tej granicy czekanie trafiało do korzeniowego
            `Suspense` w `App.tsx`, który zdejmuje cały layout na rzecz
            pełnoekranowego znaku startu — przy każdym pierwszym wejściu
            w trasę z osobnego kawałka. */}
          <Suspense fallback={<EkranStartowy uklad="w-tresci" />}>
            <Outlet />
          </Suspense>
        </main>
      </div>

      {/* Paleta poleceń ⌘K — nawigacja po panelu (Aurora DS) */}
      <CommandPalette
        items={paletteItems}
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
      />

      {/* NexusChatWidget is now rendered globally via AppProviders */}
    </div>
  );
}
